<template>
  <div id="video">
    <div v-if="!browserSupported && videoSourceLoaded" id="not-suporrted">
      <p>このブラウザーは配信を見ることができません</p>
    </div>
    <div class="video-area">
      <video id="video-tile"></video>
    </div>
    <audio id="audio-view"></audio>
  </div>
</template>

<script>
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';

import { firebase } from '@/plugins/firebase';
import * as meetingServices from '@/services/meetingServices';
const functions = firebase.app().functions('asia-northeast1');

export default {
  name: 'ChimeVideo',
  props: {
    thumbnailURL: {
      type: String,
      default: '',
      required: true
    },
    isCast: { type: Boolean },
  },
  computed: {
    videoClass() {
      let videoClass = '';
      return videoClass;
    }
  },
  data() {
    return {
      videoSourceLoaded: false,
      browserSupported: true,
      // REF: https://aws.github.io/amazon-ivs-player-docs/1.3.1/web/
      awsIvsVideoJsURL:
        'https://player.live-video.net/1.3.1/amazon-ivs-player.min.js',
      videoCheckStateIntervalID: '',
      player: null,
      meetingSession: null,
      
      meeting: null,
      attendee: null,
      liveId: null,
      showVideoSelf: false,
      devices: {
        audioInputDevices: {},
        audioOutputDevices: {},
        videoInputDevices: {},
      }
    };
  },
  async mounted() {
    this.liveId = this.$route.params.liveId;
  },
  methods: {
    async startMeeting() {
      await this.createMeeting();
      await this.createAttendee();

      const logger = new ConsoleLogger('ChimeMeetingLogs', LogLevel.WARN);
      const deviceController = new DefaultDeviceController(logger);

      const configuration = new MeetingSessionConfiguration(this.meeting, this.attendee);
      this.meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

      await this.setDeviceInfo();

      if (this.devices.audioInputDevices.length > 0) {
        if (this.isCast) {
          for (let audioInputDevice of this.devices.audioInputDevices) {
            if (audioInputDevice.label.includes('NDI')) {
              await this.meetingSession.audioVideo.chooseAudioInputDevice(audioInputDevice.deviceId);
            }
          }
        } else {
          await this.chooseAudioInputDevice(this.findDefaultDeviceId(this.devices.audioInputDevices));
        }
      }
      if (this.devices.audioOutputDevices.length > 0) {
        await this.chooseAudioOutputDevice(this.findDefaultDeviceId(this.devices.audioOutputDevices));
      }
      if (this.isCast && this.devices.videoInputDevices.length > 0) {
        for (let videoInputDevice of this.devices.videoInputDevices) {
          if (videoInputDevice.label.includes('NDI')) {
            await this.meetingSession.audioVideo.chooseVideoInputDevice(videoInputDevice.deviceId);
          }
        }
      }

      const observer = {
        videoTileDidUpdate: tileState => {
          if (!tileState.boundAttendeeId) {
            return;
          }

          if ((!this.isCast && tileState.localTile) || (this.isCast && !tileState.localTile)) {
            return;
          }

          const videoElement = document.getElementById("video-tile");
          if (videoElement) {
            this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, videoElement);
          }
        }
      }

      const audioElement = document.getElementById('audio-view');
      this.meetingSession.audioVideo.bindAudioElement(audioElement);

      this.meetingSession.audioVideo.addObserver(observer);
      this.meetingSession.audioVideo.addContentShareObserver(observer);
      this.meetingSession.audioVideo.start();
      this.meetingSession.audioVideo.startLocalVideoTile();
    },
    findDefaultDeviceId(devices) {
      let defaultItem = null;
      for (let item of devices) {
        if (item.deviceId == 'default') {
          defaultItem = item;
          break;
        }
      }

      if (defaultItem) {
        for (let item of devices) {
          if (item.deviceId != 'default' && item.groupId == defaultItem.groupId) {
            return item.deviceId;
          }
        }
      }
      return null;
    },
    async setDeviceInfo() {
      this.devices.audioInputDevices = await this.meetingSession.audioVideo.listAudioInputDevices();
      this.devices.audioOutputDevices = await this.meetingSession.audioVideo.listAudioOutputDevices();
      this.devices.videoInputDevices = await this.meetingSession.audioVideo.listVideoInputDevices();
      this.$emit("device-info", this.devices);
    },
    async chooseAudioInputDevice(deviceId) {
      for (let audioInputDevice of this.devices.audioInputDevices) {
        if (audioInputDevice.deviceId == deviceId) {
          await this.meetingSession.audioVideo.chooseAudioInputDevice(deviceId);
          return;
        }
      }
      await this.setDeviceInfo();
    },
    async chooseAudioOutputDevice(deviceId) {
      for (let audioOutputDevice of this.devices.audioOutputDevices) {
        if (audioOutputDevice.deviceId == deviceId) {
          await this.meetingSession.audioVideo.chooseAudioOutputDevice(deviceId);
          return;
        }
      }
      await this.setDeviceInfo();
    },
    async chooseVideoInputDevice(deviceId) {
      for (let videoInputDevice of this.devices.videoInputDevices) {
        if (videoInputDevice.deviceId == deviceId) {
          await this.meetingSession.audioVideo.chooseVideoInputDevice(deviceId);
          return;
        }
      }
      await this.setDeviceInfo();
      await this.meetingSession.audioVideo.chooseVideoInputDevice(deviceId);
    },
    switchMute(flag) {
      if (flag) {
        this.meetingSession.audioVideo.realtimeMuteLocalAudio();
      } else {
        this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      }
    },
    async createMeeting() {
      let meetingDoc = await meetingServices.getMeeting(this.liveId);

      if (meetingDoc) {
        this.meeting = meetingDoc.meeting;
      } else if (this.isCast) {
        const oncall = functions.httpsCallable("createMeeting");
        const result =  await oncall();
        this.meeting = result.data.Meeting;
        await meetingServices.updateMeetingDoc(this.liveId, this.meeting);
      }
    },
    async createAttendee() {
      if (this.meeting) {
        try {
          const oncall = functions.httpsCallable("createAttendee");
          const result =  await oncall(this.meeting.MeetingId);
          this.attendee = result.data.Attendee;
        } catch (error) {
          if (this.isCast) {
            await this.deleteMeeting();
            await this.createMeeting();
            await this.createAttendee();
          } else {
            setTimeout(async () => {
              await this.createAttendee();
            })
          }
        }
      }
    },
    async deleteMeeting() {
      if (this.meeting && this.isCast) {
        const oncall = functions.httpsCallable("deleteMeeting");
        await oncall(this.meeting.MeetingId);

        await meetingServices.deleteMeeting(this.liveId);

        this.meeting = null;
        this.attendee = null;
      }
    },
  },
  async beforeDestroy() {
    this.deleteMeeting();
  }
};
</script>

<style lang="scss" scoped>
#video {
  margin: auto;
  @include touch {
    height: calc(100vw*2/3);
  }
  /* height: 100%; */

  width: 100%;
  #amazonIvsVideojs {
    width: 100%;
    /* height: 100%; */
    background: black;
    @include touch {
      height: calc(100vw*2/3);
    }
  /* margin: auto;
  height: 100%;
  width: 100%;
  .video-area {
    position: relative;
    width: 100%;
    height: 100%;
    background: black;
    #video-tile {
      width: 100%;
      height: 100%;
      transform: rotateY(0) !important;
    } */
  }
  .blur {
    filter: blur(5px) sepia(1);
  }
  #no-source {
    margin: auto;
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 3;
    color: $primary;
    display: flex;
    justify-content: center;
    align-items: center;
    > p {
      text-align: center;
      font-size: medium;
      font-weight: bold;
    }
    /*margin: auto;
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 3;
    color: $primary;
    display: flex;
    justify-content: center;
    align-items: center;
    > p {
      text-align: center;
      font-size: medium;
      font-weight: bold;
    }*/
  }
  #not-suporrted {
    margin: auto;
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 3;
    color: $primary;
    display: flex;
    justify-content: center;
    align-items: center;
    > p {
      text-align: center;
      font-size: x-large;
      font-weight: bold;
    }
  }
}
</style>
